import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import {
  AppRootState,
  navigateToEnrollmentModule,
  navigateToPatientRequests,
  navigateToRequests,
  navigateToServiceRequest,
  tenancyFeatureSelector
} from '../redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ConnectedHcpGridWithGQL } from './HcpGridWithGQL';
import {
  ActionColumnActions,
  ROUTE_PATHS,
  actionsColumnDefinition,
  dateColumnDefinition,
  dobDateColumnFormat,
  featureGridColumnsSelector,
  filterGridComparator,
  formatStatusText,
  generalColumnDefinition,
  getConfigurationDataKey,
  getFeatureGridColumnProps,
  idColumnDefinition,
  nameColumnDefinition,
  nextStepColumnWithoutTooltip,
  prescriberColumnDefinition,
  requestStatusColumnDefinition,
  requestStatusValueFormatter,
  serviceRequestAlertColumnDefinition
} from '../utils';
import { AgGridColumn } from 'ag-grid-react';
import { AnyType } from '../interfaces';
import { DashboardTile } from './DashboardTile';
import { useServiceRequestInfoQuery } from '../graphql';
import { RowNode } from 'ag-grid-community';
import { GridFilters } from './GridFilters';
import { FilterInterface, RequestsTable, StatusDescription } from './RequestsTable';
import { dashboardfilterComparator } from '../utils/dashbaord-filter-utils';
import { EnrollmentModuleViewState } from '../views/EnrollmentModuleView';

interface RequestsTableProps {
  enableExternalFilters?: boolean;
  filteredStatus?: string;
}

// Column selector from the utils
const columnSelector = featureGridColumnsSelector(getConfigurationDataKey('service-request.table'));

const DashboardTable = (props: RequestsTableProps) => {
  // Get programId and drugList from Redux store
  const programId = Number(useSelector((state: AppRootState) => state.auth.session?.user?.ProgramId));
  const drugList: AnyType = useSelector((state: AppRootState) => state?.app?.entities?.drugList);

  // State hooks for filter and status management
  const [gridFilter, setGridFilter] = useState<FilterInterface>({ strictCompare: true });
  const [filter, setFilter] = useState<string>();
  const [filterList, setFilterList] = useState<Set<string>>(new Set([]));
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [statusDescriptions, setStatusDescriptions] = useState<any[]>([]);
  const requestsFilterMapping: AnyType = useSelector<AnyType>(tenancyFeatureSelector('sr.filters_v1.5'));
  const [actionRequiredCount, setActionRequiredCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [gridData, setGridData] = useState<any[]>([]);
  const dispatch = useDispatch();
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({}); // State for status counts

  // Update gridFilter whenever filterList or filter changes
  useEffect(() => {
    setGridFilter({ ...gridFilter, secondaryFilterList: filterList, primaryFilter: filter });
  }, [filterList, filter]);

  useEffect(() => {
    if (statusCounts) {
      const actionRequired = statusCounts['Action Required'] || 0;
      const pending = statusCounts['Pending'] || 0;
      const completed = statusCounts['Completed'] || 0;
      const incomplete_enrollments = statusCounts['Incomplete Enrollment'] || 0;
      setActionRequiredCount(actionRequired);
      setPendingCount(pending);
      setCompletedCount(completed);
      setIncompleteCount(incomplete_enrollments);
    }
  }, [statusCounts]);

  // Update statusDescriptions and other grid-related logic on data load
  const onDataLoad = (data: any[]) => {
    setGridData(data); // Store the grid data

    // Assuming you fetch status descriptions or derive them from data
    const descriptions: any = data.map((row) => {
      return { status: formatStatusText(row, requestsFilterMapping) };
    });
    // Now set the status descriptions based on the data
    setStatusDescriptions(descriptions); // Update the state with new descriptions
    calculateStatusCounts(data); // Calculate counts based on statusText
  };

  const calculateStatusCounts = (data: any[]) => {
    const counts: Record<string, number> = {};
    data.forEach((row) => {
      const filteredStatus = formatStatusText(row, requestsFilterMapping);
      if (filteredStatus) {
        counts[filteredStatus] = (counts[filteredStatus] || 0) + 1;
      }
    });
    setStatusCounts(counts);
  };

  // Function to handle filter change
  const onFilterChange = (newFilter: string) => {
    setFilter(newFilter); // Update the filter
    setFilterList((prevFilterList) => {
      const newList = new Set(prevFilterList);
      if (newList.has(newFilter)) {
        newList.delete(newFilter);
      } else {
        newList.add(newFilter);
      }
      return newList;
    });
  };

  // Update grid filter based on status descriptions
  const filterProps = {
    isFilterActive: true,
    filterComparator: function (node: RowNode, filter?: FilterInterface) {
      return dashboardfilterComparator(node, filter, statusDescriptions, requestsFilterMapping);
    },
    filterValue: gridFilter
  };

  const columns = useSelector(columnSelector);

  // Ensure we have columns and drugList before rendering
  if (!columns || !drugList) {
    return (
      <div>Loading...</div> // or some other fallback UI
    );
  }

  const handleViewClick = (data: any) => {
    if (data && data.patientId && data.patientEnrollmentGuid) {
      const enrollmentRouteState: EnrollmentModuleViewState = {
        patientId: data.patientId?.toString(),
        enrollmentId: data.patientEnrollmentGuid?.toString()
      };
      return dispatch(navigateToEnrollmentModule(enrollmentRouteState));
    } else {
      return dispatch(navigateToPatientRequests(data.patientId));
    }
  };
  return (
    <>
      <DashboardTile
        actionRequiredCount={actionRequiredCount}
        pendingCount={pendingCount}
        completedCount={completedCount}
        incompleteCount={incompleteCount}
        onFilterChange={onFilterChange}
      />
      <div className='site-background'>
        <Row className='mb-3'>
          <Col className='recent-request'> RECENT REQUESTS</Col>
          <Col className='d-flex justify-content-end'>
            <Button
              size='sm'
              variant='outline-secondary'
              className='px-4'
              onClick={() => dispatch(navigateToRequests())}>
              View All
            </Button>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <ConnectedHcpGridWithGQL
              className='grid-wrapper request-grid'
              descriptionType='SrHubStatusDescription'
              {...filterProps}
              queryHook={useServiceRequestInfoQuery}
              onDataLoad={(data: any[]) => {
                onDataLoad(data); // Call onDataLoad when data is loaded
              }}
              queryOptions={{
                variables: {
                  programId,
                  include_draftEnrollments: true
                }
              }}
              pagination
              gridOptions={{
                suppressCellSelection: true // Disable cell selection
              }}>
              {/* AG-Grid Columns */}
              {/* <AgGridColumn {...serviceRequestAlertColumnDefinition()} /> */}
              <AgGridColumn
                headerName=''
                field=''
                valueGetter={(params) => {
                  const formattedStatus = formatStatusText(params.data, requestsFilterMapping);
                  return formattedStatus;
                }}
                cellRenderer={(params) => {
                  const status = params.value;
                  if (status === 'Action Required') {
                    return `<span style="color:#C82333"><i class='fa fa-exclamation-triangle'></i></span>`;
                  } else if (status === 'Pending') {
                    return `<span style="color:#0084A9"><i class='fa fa-stars'></i></div>`;
                  } else if (status === 'Completed') {
                    return `<span style="color:#218838"><i class='fa fa-badge-check'></i></div>`;
                  } else if (status === 'Incomplete Enrollment') {
                    return `<span style="color:#FFBF00"><i class='fa fa-user-injured'></i></div>`;
                  }

                  return '';
                }}
                tooltipField='statusIcon'
                resizable={false}
                sortable={false}
                width={40}
              />

              <AgGridColumn
                field='Updated'
                valueGetter={(params) => {
                  const updatedDate = params.data.updatedAlertDate;
                  if (updatedDate) {
                    const date = new Date(updatedDate);
                    return date.toLocaleDateString();
                  }
                  return '';
                }}
                tooltipField='Updated'
                resizable={true}
                {...generalColumnDefinition()}
                {...nameColumnDefinition(columns.updatedAlertDate)}
              />
              <AgGridColumn
                field='Patient Name'
                valueGetter={(params) => `${params.data.patientFirstName} ${params.data.patientLastName}`}
                tooltipField='patientFirstName'
                resizable={true}
                {...generalColumnDefinition()}
                {...nameColumnDefinition(columns.fullName)}
              />

              <AgGridColumn
                field='Date of Birth'
                valueGetter={(params) => {
                  return new Date(params.data.patientDOB).toLocaleDateString();
                }}
                tooltipField='Date of Birthh'
                resizable={true}
                {...generalColumnDefinition()}
                {...nameColumnDefinition(columns.DateofBirth)}
              />
              <AgGridColumn
                field='sRDrugName'
                headerName='Drug'
                tooltipField='Drug Name'
                resizable={true}
                {...generalColumnDefinition()}
              />
              <AgGridColumn
                field='requestType'
                tooltipField='requestType'
                resizable={true}
                {...generalColumnDefinition()}
                //{...nameColumnDefinition(columns.requestType)}
              />

              <AgGridColumn
                field='statusText'
                tooltipField='statusText'
                resizable={true}
                {...generalColumnDefinition()}
                {...requestStatusColumnDefinition('patientHubStatus', columns.status, requestsFilterMapping)}
                {...getFeatureGridColumnProps(columns.status)}
              />
              <AgGridColumn
                headerName=''
                cellRendererFramework={(params: any) => (
                  <div className='custom-links'>
                    <Button
                      variant='link'
                      style={{ textDecoration: 'none' }}
                      className='text-secondary fw-normal custom-links'
                      onClick={() => handleViewClick(params.data)}>
                      View
                    </Button>
                  </div>
                )}
                resizable={false}
                sortable={false}
              />
            </ConnectedHcpGridWithGQL>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default DashboardTable;
